import { createI18n, type VueMessageType } from 'vue-i18n';

type NamespaceMessages = Record<string, VueMessageType>;
type JsonModule = { default: NamespaceMessages };
type LocaleMessages = Record<string, NamespaceMessages | Record<string, NamespaceMessages>>;

/**
 * Satellite custom-field components translate through @nova/core's t() with `core.*` and
 * satellite `global.*` / `customForms.*` keys, so both bundles must live in this instance.
 * App locales load last so Juno's own namespaces win on collision.
 */
const coreLocales = import.meta.glob<JsonModule>('@nova/core-locales/**/*.json', { eager: true });
const satelliteLocales = import.meta.glob<JsonModule>('@satellite/locales/**/*.json', {
  eager: true
});
const appLocales = import.meta.glob<JsonModule>('@/locales/**/*.json', { eager: true });

function loadLocaleMessages() {
  const messages: Record<string, LocaleMessages> = {};

  const all = [
    ...Object.entries(coreLocales).map(([path, localeModule]) => ({
      path,
      localeModule,
      isCore: true
    })),
    ...Object.entries(satelliteLocales).map(([path, localeModule]) => ({
      path,
      localeModule,
      isCore: false
    })),
    ...Object.entries(appLocales).map(([path, localeModule]) => ({
      path,
      localeModule,
      isCore: false
    }))
  ];

  for (const { path, localeModule, isCore } of all) {
    const match = path.match(/\/(?:core-)?locales\/([a-zA-Z-]+)\/([a-zA-Z-]+)\.json$/);
    if (!match) {
      console.warn('Unmatched locale path:', path);
      continue;
    }
    const [, locale, namespace] = match;
    messages[locale] ??= {};

    if (isCore) {
      const core = (messages[locale].core ??= {}) as Record<string, NamespaceMessages>;
      core[namespace] = localeModule.default;
    } else {
      messages[locale][namespace] = localeModule.default;
    }
  }

  return messages;
}

const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: 'en',
  fallbackLocale: 'en',
  messages: loadLocaleMessages()
});

export default i18n;
